// #region Imports
import { DOCUMENT, Service, Signal, WritableSignal, inject, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { BootstrapService } from '../../services/bootstrap.service';
import { SITE_THEMES, SiteThemeKey } from './site-themes.registry';
// #endregion Imports

// #region TypesAndInterfaces
// Dopo questo tempo l'app si mostra con i colori base: mai uno schermo bianco per un CDN lento.
const THEME_LOAD_TIMEOUT_MS: number = 3000;
// Segna i <link> aggiunti da questo servizio, per toglierli quando il sito cambia.
const THEME_LINK_ATTRIBUTE: string = 'data-site-theme';
// #endregion TypesAndInterfaces

/**
 * Applica i colori del sito all'avvio, prima che compaia la prima pagina.
 *
 * Il sito lo risolve BootstrapService (ID_Site / ID_SiteMaster dall'utente, dal dominio senza
 * utente): qui non si legge mai l'hostname una seconda volta. Gli id finiscono negli attributi
 * data-site e data-site-master di <html>, e i file di sito del registro, caricati a cascata,
 * ridefiniscono i token --app-color-* su quei selettori.
 *
 * È parte del bootstrap, non una cerimonia: AppComponent mostra le pagine solo quando isReady()
 * è vero, così la prima pagina compare già con i colori del sito.
 */
@Service()
export class SiteThemeService {
  // #region Signals
  // Letto dal template di AppComponent: deve essere un signal, un booleano semplice cambiato dentro
  // una Promise non ridisegnerebbe un componente OnPush (schermo bianco).
  private readonly _isReady: WritableSignal<boolean> = signal<boolean>(false);
  readonly isReady: Signal<boolean> = this._isReady.asReadonly();
  // #endregion Signals

  // #region DependencyInjection
  private readonly document: Document = inject(DOCUMENT);
  private readonly bootstrapService: BootstrapService = inject(BootstrapService);
  // #endregion DependencyInjection

  // #region PublicMethods
  /**
   * Imposta gli attributi del sito e carica i suoi file di colori. Non rigetta mai: con un errore
   * o in timeout restano i colori base e il gate si apre comunque.
   * Va chiamata dopo che BootstrapService ha caricato l'utente.
   */
  async applySiteTheme(): Promise<void> {
    try {
      const siteMasterId: number | null = this.bootstrapService.siteMasterId();
      const siteId: number | null = this.bootstrapService.siteId();

      // Il bootstrap si ripete a ogni login e logout: con lo stesso sito non si ricarica nulla.
      if (this.isApplied(siteMasterId, siteId)) {
        return;
      }

      this.removeThemeLinks();
      this.setRootAttribute('data-site-master', siteMasterId);
      this.setRootAttribute('data-site', siteId);

      // Prima il master, poi lo store: il selettore dello store è più specifico e vince sempre,
      // quindi i due file si possono caricare insieme.
      const keys: SiteThemeKey[] = [];
      if (siteMasterId !== null) {
        keys.push(`master:${siteMasterId}`);
      }
      if (siteId !== null) {
        keys.push(`site:${siteId}`);
      }
      const urls: string[] = keys
        .map((key: SiteThemeKey): string | null => this.themeUrl(key))
        .filter((url: string | null): url is string => url !== null);

      const results: PromiseSettledResult<void>[] = await Promise.allSettled(
        urls.map((url: string): Promise<void> => this.loadStylesheet(url))
      );
      for (const result of results) {
        if (result.status === 'rejected') {
          console.error('[SiteTheme] override non applicato, restano i colori base', result.reason);
        }
      }
    } catch (error: unknown) {
      // Meglio i colori base che uno schermo bianco: l'errore resta visibile in console.
      console.error('[SiteTheme] override non applicato, restano i colori base', error);
    } finally {
      this._isReady.set(true);
    }
  }

  /**
   * Apre il gate senza toccare i colori: per i percorsi in cui il bootstrap non risolve il sito
   * (login-from-admin), dove prima la classe del tema restava quella che era.
   */
  ensureReady(): void {
    this._isReady.set(true);
  }
  // #endregion PublicMethods

  // #region PrivateMethods
  private themeUrl(key: SiteThemeKey): string | null {
    const url: string | undefined = SITE_THEMES[key];
    // Versione dell'app in query: i file di sito non hanno hash nel nome, senza questo un colore
    // cambiato arriverebbe agli utenti solo alla scadenza della cache.
    return url ? `${url}?v=${encodeURIComponent(environment.version)}` : null;
  }

  private isApplied(siteMasterId: number | null, siteId: number | null): boolean {
    const root: HTMLElement = this.document.documentElement;
    return this._isReady()
      && root.getAttribute('data-site-master') === this.attributeValue(siteMasterId)
      && root.getAttribute('data-site') === this.attributeValue(siteId);
  }

  private attributeValue(id: number | null): string | null {
    return id === null ? null : String(id);
  }

  private setRootAttribute(name: string, id: number | null): void {
    const value: string | null = this.attributeValue(id);
    if (value === null) {
      this.document.documentElement.removeAttribute(name);
    } else {
      this.document.documentElement.setAttribute(name, value);
    }
  }

  private removeThemeLinks(): void {
    this.document.head.querySelectorAll(`link[${THEME_LINK_ATTRIBUTE}]`).forEach((link: Element): void => link.remove());
  }

  private loadStylesheet(url: string): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (reason: Error) => void): void => {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute(THEME_LINK_ATTRIBUTE, '');
      const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
        // Tolto al timeout: un foglio che arrivasse più tardi cambierebbe i colori a metà sessione.
        link.remove();
        reject(new Error(`timeout ${url}`));
      }, THEME_LOAD_TIMEOUT_MS);
      link.onload = (): void => {
        clearTimeout(timer);
        resolve();
      };
      link.onerror = (): void => {
        clearTimeout(timer);
        link.remove();
        reject(new Error(`load failed ${url}`));
      };
      this.document.head.appendChild(link);
    });
  }
  // #endregion PrivateMethods
}
